import dayjs, { type Dayjs } from 'dayjs';

// Crop Year picker rules (113601, "Add Field Details"). They live here rather than in
// `<CropYearField>` so the component never calls dayjs itself (ADR-0007: "All formatting goes
// through one module (`lib/date`)") and so both rules are testable against a fixed clock.

/**
 * The widest Crop Year, as a month offset from the chosen edge. `+11` spans **12 calendar months
 * inclusive** (Aug'26 - Jul'27). The AC's "more than 12 months later" is read against the outcome
 * it states in the same breath — "so no range beyond 12 months can be created" — which makes
 * Aug'26 - Aug'27 (13 months) the first refusal. Same reading as `spec.md` and grilling decision 20.
 */
const MAX_SPAN_MONTHS = 11;

/** A chosen month as the AC's `mmm'yy` — "Jan'26". */
export function formatCropMonth(month: Dayjs): string {
  return month.format("MMM'YY");
}

/**
 * Which months the Crop Year picker refuses: every month before the current one, and — once either
 * edge has been marked — every month further than `MAX_SPAN_MONTHS` from it.
 *
 * The distance is absolute because antd reports `info.from` as the edge the Grower marked *first*,
 * "not care about what field user click": clicking into the end input first makes that the anchor,
 * and a signed comparison would leave the start uncapped.
 *
 * Shaped to antd's `disabledDate` so it can be passed straight in — `now` is a third parameter antd
 * never supplies, which is what lets the tests pin the clock.
 */
export function isCropYearMonthDisabled(
  month: Dayjs,
  info: { from?: Dayjs },
  now: Dayjs = dayjs(),
): boolean {
  if (month.isBefore(now, 'month')) return true;
  if (!info.from) return false;
  const span = month.startOf('month').diff(info.from.startOf('month'), 'month');
  return Math.abs(span) > MAX_SPAN_MONTHS;
}
